<template>
  <v-card class="date-filter-card mb-4" elevation="1" outlined>
    <v-card-text class="py-2 px-3">
      <v-skeleton-loader
        v-if="loading"
        type="list-item"
        class="mb-2"
      ></v-skeleton-loader>
      <div v-else class="d-flex align-center flex-wrap">
        <!-- Card Title with Icon -->
        <div class="d-flex align-center mr-3 filter-title">
          <v-icon color="primary" small class="mr-1">mdi-calendar-filter</v-icon>
          <span class="subtitle-2 font-weight-medium">Date Filter</span>
        </div>
        
        <!-- Date Range Buttons -->
        <v-btn-toggle 
          v-model="localRangeKey" 
          dense 
          mandatory 
          class="mr-3 my-1 date-toggle" 
          @change="onRangeChange"
          hide-details
          background-color="grey lighten-4"
          color="primary"
        >
          <v-btn x-small value="today" class="px-2">Today</v-btn>
          <v-btn x-small value="this_week" class="px-2">Week</v-btn>
          <v-btn x-small value="this_month" class="px-2">Month</v-btn>
          <v-btn x-small value="this_year" class="px-2">Year</v-btn>
        </v-btn-toggle>
        
        <v-spacer></v-spacer>
        
        <!-- Date Range Navigation and Display with Selector -->
        <div class="d-flex align-center my-1">
          <!-- Navigation Arrows -->
          <v-btn icon x-small class="mr-1" @click="navigateRange(-1)" :disabled="loading">
            <v-icon x-small>mdi-chevron-left</v-icon>
          </v-btn>
          
          <!-- Date Range Display -->
          <div class="date-range-display">
            <v-menu
              v-model="dateRangeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip 
                  small 
                  outlined 
                  class="font-weight-regular" 
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left x-small>mdi-calendar-range</v-icon>
                  {{ formatDisplayDate(rangeStart) }} - {{ formatDisplayDate(rangeEnd) }}
                </v-chip>
              </template>
              <v-card>
                <v-card-text class="pa-2">
                  <v-row dense>
                    <v-col cols="6">
                      <v-date-picker
                        v-model="localRangeStartDate"
                        no-title
                        scrollable
                        color="primary"
                        :max="getMaxDate(localRangeEndDate)"
                      ></v-date-picker>
                    </v-col>
                    <v-col cols="6">
                      <v-date-picker
                        v-model="localRangeEndDate"
                        no-title
                        scrollable
                        color="primary"
                        :min="localRangeStartDate"
                        :max="getTodayDate()"
                      ></v-date-picker>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    @click="dateRangeMenu = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    @click="confirmDateRange"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>
          
          <!-- Navigation Arrows -->
          <v-btn icon x-small class="ml-1" @click="navigateRange(1)" :disabled="loading">
            <v-icon x-small>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  getRangeForKey as getRangeForKeyUtil,
  formatDate as formatDateUtil,
} from '@Company/config/activity-utils';

export default {
  name: 'DateFilterComponent',
  props: {
    rangeKey: {
      type: String,
      default: 'this_month'
    },
    rangeStart: {
      type: String,
      default: null
    },
    rangeEnd: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localRangeKey: this.rangeKey,
      dateRangeMenu: false,
      localRangeStartDate: this.rangeStart,
      localRangeEndDate: this.rangeEnd
    };
  },
  computed: {
    monthPickerDisplay() {
      // Show YYYY-MM of the current range start
      if (!this.rangeStart) return '';
      const d = new Date(this.rangeStart);
      if (isNaN(d)) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    }
  },
  watch: {
    rangeKey(newVal) {
      this.localRangeKey = newVal;
    },
    rangeStart(newVal) {
      this.localRangeStartDate = newVal;
    },
    rangeEnd(newVal) {
      this.localRangeEndDate = newVal;
    }
  },
  methods: {
    onRangeChange() {
      const { start, end } = this.getRangeForKey(this.localRangeKey);
      this.localRangeStartDate = this.formatDate(start);
      this.localRangeEndDate = this.formatDate(end);
      this.$emit('range-change', {
        rangeKey: this.localRangeKey,
        rangeStart: this.localRangeStartDate,
        rangeEnd: this.localRangeEndDate
      });
    },
    confirmDateRange() {
      // Only emit when both dates are selected and confirm button is clicked
      if (this.localRangeStartDate && this.localRangeEndDate) {
        this.localRangeKey = 'custom'; // Set to custom when using date picker
        this.$emit('date-range-change', {
          rangeKey: 'custom',
          rangeStart: this.localRangeStartDate,
          rangeEnd: this.localRangeEndDate
        });
        this.dateRangeMenu = false;
      }
    },
    navigateRange(direction) {
      // Navigate based on current range key (week, month, year)
      const currentStart = new Date(this.rangeStart);
      const currentEnd = new Date(this.rangeEnd);
      let newStart, newEnd;
      
      switch (this.localRangeKey) {
        case 'today':
          // Move one day
          newStart = new Date(currentStart);
          newStart.setDate(newStart.getDate() + direction);
          newEnd = new Date(newStart);
          break;
        case 'this_week':
          // Move one week
          newStart = new Date(currentStart);
          newStart.setDate(newStart.getDate() + (direction * 7));
          newEnd = new Date(currentEnd);
          newEnd.setDate(newEnd.getDate() + (direction * 7));
          break;
        case 'this_month':
          // Move one month
          newStart = new Date(currentStart);
          newStart.setMonth(newStart.getMonth() + direction);
          newEnd = new Date(currentEnd);
          newEnd.setMonth(newEnd.getMonth() + direction);
          break;
        case 'this_year':
          // Move one year
          newStart = new Date(currentStart);
          newStart.setFullYear(newStart.getFullYear() + direction);
          newEnd = new Date(currentEnd);
          newEnd.setFullYear(newEnd.getFullYear() + direction);
          break;
        case 'custom':
            {
            // For custom range, calculate the difference in days and shift by that amount
            let diffDays = Math.round((currentEnd - currentStart) / (1000 * 60 * 60 * 24));
            newStart = new Date(currentStart);
            newStart.setDate(newStart.getDate() + (direction * diffDays));
            newEnd = new Date(currentEnd);
            newEnd.setDate(newEnd.getDate() + (direction * diffDays));
            }
          
          break;
        default:
          return; // Do nothing for unknown range types
      }
      
      // Ensure we don't navigate to future dates
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      
      if (newEnd > today) {
        // If we're trying to navigate to the future, cap at today
        if (direction > 0) {
          // Only cap when moving forward
          const { start, end } = this.getRangeForKey(this.localRangeKey);
          newStart = start;
          newEnd = end;
        }
      }
      
      this.localRangeStartDate = this.formatDate(newStart);
      this.localRangeEndDate = this.formatDate(newEnd);
      
      // Emit the change
      this.$emit('date-range-change', {
        rangeKey: this.localRangeKey,
        rangeStart: this.localRangeStartDate,
        rangeEnd: this.localRangeEndDate
      });
    },
    getTodayDate() {
      // Return today's date in YYYY-MM-DD format for max date restriction
      const today = new Date();
      return this.formatDate(today);
    },
    getMaxDate(endDate) {
      // Return the lesser of endDate or today
      const today = this.getTodayDate();
      if (!endDate) return today;
      return endDate < today ? endDate : today;
    },
    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date)) return '';
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getRangeForKey(key) {
      return getRangeForKeyUtil(key);
    },
    formatDate(d) {
      return formatDateUtil(d);
    }
  }
};
</script>

<style scoped>
.date-filter-card {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.date-filter-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.filter-title {
  min-width: 100px;
}

.date-toggle {
  border-radius: 4px;
  overflow: hidden;
}

.month-picker {
  font-size: 0.8rem;
}

.date-range-display {
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .filter-title {
    margin-bottom: 8px;
    width: 100%;
  }
  
  .date-range-display {
    margin-top: 8px;
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
